/**
 * Support API Service
 * 
 * API calls for support tickets and messages.
 */

import { apiClient } from "./apiClient";

export interface CreateTicketRequest {
  subject: string;
  message: string;
  priority?: "low" | "normal" | "high" | "urgent";
  reference_id?: string;
  reference_type?: string;
}

export interface AddMessageRequest {
  message: string;
}

export interface TicketResponse {
  id: string;
  subject: string;
  status: string;
  priority: string;
  reference_id?: string | null;
  reference_type?: string | null;
  created_at: string;
  updated_at: string;
  resolved_at?: string | null;
  message_count: number;
}

export interface MessageResponse {
  id: string;
  ticket_id: string;
  message: string;
  is_internal: boolean;
  created_at: string;
  is_from_user: boolean;
  is_from_admin: boolean;
}

export interface TicketDetailResponse {
  ticket: TicketResponse;
  messages: MessageResponse[];
}

export interface TicketListResponse {
  tickets: TicketResponse[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Create a new support ticket
 */
export async function createTicket(data: CreateTicketRequest): Promise<TicketResponse> {
  const response = await apiClient.post<TicketResponse>("/user/support/tickets", data);
  return response.data;
}

/**
 * List user tickets
 */
export async function listUserTickets(params?: {
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<TicketResponse[]> {
  const response = await apiClient.get<TicketResponse[]>("/user/support/tickets", {
    params,
  });
  return response.data;
}

/**
 * Get ticket details
 */
export async function getTicketDetails(ticketId: string): Promise<TicketDetailResponse> {
  const response = await apiClient.get<TicketDetailResponse>(
    `/user/support/tickets/${ticketId}`,
  );
  return response.data;
}

/**
 * Add message to ticket
 */
export async function addMessageToTicket(
  ticketId: string,
  data: AddMessageRequest,
): Promise<MessageResponse> {
  const response = await apiClient.post<MessageResponse>(
    `/user/support/tickets/${ticketId}/messages`,
    data,
  );
  return response.data;
}

/**
 * List all tickets (admin only)
 */
export async function listAllTickets(params?: {
  status?: string;
  user_id?: string;
  limit?: number;
  offset?: number;
}): Promise<TicketListResponse> {
  const response = await apiClient.get<TicketListResponse>("/admin/support/tickets", {
    params,
  });
  return response.data;
}

/**
 * Get ticket details (admin, includes internal messages)
 */
export async function getTicketDetailsAdmin(ticketId: string): Promise<TicketDetailResponse> {
  const response = await apiClient.get<TicketDetailResponse>(
    `/admin/support/tickets/${ticketId}`,
  );
  return response.data;
}

/**
 * Admin reply to ticket
 */
export async function adminReply(
  ticketId: string,
  data: { message: string; is_internal?: boolean },
): Promise<MessageResponse> {
  const response = await apiClient.post<MessageResponse>(
    `/admin/support/tickets/${ticketId}/reply`,
    data,
  );
  return response.data;
}

/**
 * Update ticket status (admin only)
 */
export async function updateTicketStatus(
  ticketId: string,
  data: { status: string; priority?: string },
): Promise<TicketResponse> {
  const response = await apiClient.patch<TicketResponse>(
    `/admin/support/tickets/${ticketId}/status`,
    data,
  );
  return response.data;
}

